import { useState, useRef, useEffect, type ReactNode } from "react";
import { Link, useLocation, useNavigationType } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { Fit2LiftWordmark } from "@/components/branding/Fit2LiftWordmark";

export interface AppShellProps {
  /** Sidebar content (nav links, etc.) */
  sidebar?: ReactNode;
  /**
   * Team member layout: no drawer sidebar; sticky top bar with wordmark + compact account menu.
   * Bottom navigation should be passed separately (`bottomNavigation`).
   */
  layoutVariant?: "default" | "employee";
  /** Mobile primary nav — laid out in-flow under `<main>` on small screens so content never scrolls under it. Hidden from `lg` up. */
  bottomNavigation?: ReactNode;
  /** Team member laptop nav — horizontal strip inside the sticky header from `lg` up (paired with {@link layoutVariant} `employee`). */
  employeeDesktopNavigation?: ReactNode;
  /** Main content area */
  children: ReactNode;
  /** Optional header above main (e.g. page title) */
  header?: ReactNode;
  /** Optional left control in the top bar (e.g. Back) — shown beside the title on all breakpoints when sidebar is present. */
  headerLeft?: ReactNode;
  /** Optional right-side action in header (e.g. Create organization) */
  headerRight?: ReactNode;
  /** When true, `headerRight` stays visible on all breakpoints (default: desktop-only). */
  headerRightPersistent?: boolean;
  /**
   * Show a full-screen toggle in the top bar (org area). Full screen covers the top bar and page,
   * not the sidebar, and persists while navigating; it exits when this turns false.
   */
  fullscreenAvailable?: boolean;
}

function ExpandIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" aria-hidden="true">
      <polyline points="6 2 2 2 2 6" />
      <polyline points="12 2 16 2 16 6" />
      <polyline points="6 16 2 16 2 12" />
      <polyline points="12 16 16 16 16 12" />
    </svg>
  );
}

function ShrinkIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" aria-hidden="true">
      <polyline points="2 6 6 6 6 2" />
      <polyline points="16 6 12 6 12 2" />
      <polyline points="2 12 6 12 6 16" />
      <polyline points="16 12 12 12 12 16" />
    </svg>
  );
}

type FullscreenCapable = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> };
type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => void;
};

function currentFullscreenElement(): Element | null {
  const d = document as FullscreenDocument;
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}

function exitAnyFullscreen(): void {
  const d = document as FullscreenDocument;
  if (d.exitFullscreen) void d.exitFullscreen();
  else d.webkitExitFullscreen?.();
}

function fullscreenSupported(): boolean {
  if (typeof document === "undefined") return false;
  const el = document.documentElement as FullscreenCapable;
  return Boolean(el.requestFullscreen || el.webkitRequestFullscreen);
}

function UserIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

export function AppShell({
  sidebar,
  layoutVariant = "default",
  bottomNavigation,
  employeeDesktopNavigation,
  children,
  header,
  headerLeft,
  headerRight,
  headerRightPersistent = false,
  fullscreenAvailable = false,
}: AppShellProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const mainColumnRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);

  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () =>
      setIsFullscreen(Boolean(mainColumnRef.current) && currentFullscreenElement() === mainColumnRef.current);
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  // Leaving the org area ends full screen, so other pages never open without their sidebar.
  useEffect(() => {
    if (!fullscreenAvailable && currentFullscreenElement() === mainColumnRef.current) {
      exitAnyFullscreen();
    }
  }, [fullscreenAvailable]);

  // Fit2Lift staff opening an organization go straight to full screen (the iPad testing view).
  // Only on the step INTO the org area: moving around inside it, or leaving full screen there,
  // never re-enters. Browsers allow this only right after a tap or click — choosing the org —
  // so a page reload or typed address stays as it is, and the toggle still works.
  const { user_type: shellUserType } = usePermissions();
  const autoFullscreen = shellUserType === "superadmin" || shellUserType === "admin";
  const wasFullscreenAvailable = useRef(false);
  useEffect(() => {
    const entering = fullscreenAvailable && !wasFullscreenAvailable.current;
    wasFullscreenAvailable.current = fullscreenAvailable;
    if (!entering || !autoFullscreen || !fullscreenSupported() || currentFullscreenElement()) return;
    const el = mainColumnRef.current as FullscreenCapable | null;
    if (!el) return;
    try {
      if (el.requestFullscreen) void el.requestFullscreen().catch(() => {});
      else if (el.webkitRequestFullscreen) void el.webkitRequestFullscreen()?.catch?.(() => {});
    } catch {
      /* Refused without a recent tap: the page stays as it is. */
    }
  }, [fullscreenAvailable, autoFullscreen]);

  const toggleFullscreen = () => {
    if (currentFullscreenElement()) {
      exitAnyFullscreen();
      return;
    }
    const el = mainColumnRef.current as FullscreenCapable | null;
    if (!el) return;
    if (el.requestFullscreen) void el.requestFullscreen();
    else if (el.webkitRequestFullscreen) void el.webkitRequestFullscreen();
  };
  const showFullscreenToggle = fullscreenAvailable && fullscreenSupported();
  const location = useLocation();
  const navigationType = useNavigationType();
  // <main> scrolls (not the window) and stays mounted across pages, so start each new page at the
  // top — instantly, not animated. Back/Forward (POP) keeps the place the user left.
  useEffect(() => {
    if (navigationType === "POP") return;
    mainRef.current?.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [location.pathname, navigationType]);
  const { authEmail, signOut, defaultPath } = useAuth();
  const { user_type, isEmployee, role_type, isSuperadmin } = usePermissions();
  const employeeShell = layoutVariant === "employee";
  const showSidebar = Boolean(sidebar) && !employeeShell;
  const roleLine =
    user_type === "superadmin"
      ? "Super admin"
      : user_type === "admin"
        ? "Admin"
        : user_type === "orgadmin"
          ? "Organization admin"
          : user_type === "orguser" && role_type === "manager"
            ? "Manager"
            : user_type === "orguser" && role_type === "org_contact"
              ? "Organization contact"
              : user_type === "orguser" && isEmployee
                ? "Team member"
                : user_type === "orguser"
                  ? "Org user"
                  : null;

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    setSidebarOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  function renderUserMenu(popoverUpward: boolean) {
    return (
      <div className="relative shrink-0" ref={menuRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          aria-expanded={menuOpen}
          aria-haspopup="true"
          aria-label="User menu"
          className={
            "group flex items-center gap-2 rounded-[var(--radius-md)] p-2 text-left transition-all duration-200 hover:bg-[var(--color-border-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-signal-blue)] focus-visible:ring-inset " +
            (employeeShell ? "min-h-[44px] min-w-[44px] justify-center" : "")
          }
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-highlight-blue)]/50 bg-[var(--color-surface-elevated)] text-[var(--color-highlight-blue)] transition-transform duration-200 group-hover:scale-110">
            <UserIcon className="h-5 w-5" />
          </span>
          {!employeeShell ? (
            <span className="min-w-0 flex-1 text-left">
              <span className="block truncate text-sm text-[var(--color-steel-gray)]">{authEmail ?? "—"}</span>
              {roleLine ? (
                <span className="mt-0.5 block font-[var(--font-display)] text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-text-subtle)]">
                  {roleLine}
                </span>
              ) : null}
            </span>
          ) : null}
        </button>
        {menuOpen && (
          <div
            className={
              "absolute right-0 z-[100] w-[min(100vw-1rem,14rem)] rounded-[var(--radius-md)] border border-[var(--color-border-muted)] bg-[var(--color-charcoal)] py-1 " +
              (popoverUpward ? "bottom-full mb-1" : "top-full mt-1")
            }
            role="menu"
          >
            {!employeeShell ? (
              <div className="border-b border-[var(--color-border-muted)] px-3 py-2">
                <span className="block truncate text-xs text-[var(--color-steel-gray)]">{authEmail ?? "—"}</span>
                {roleLine ? (
                  <span className="mt-0.5 block font-[var(--font-display)] text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-text-subtle)]">
                    {roleLine}
                  </span>
                ) : null}
              </div>
            ) : null}
            {isSuperadmin ? (
              <Link
                to="/admin/settings"
                role="menuitem"
                className="block w-full px-4 py-2 text-left font-[var(--font-display)] text-sm text-[var(--color-steel-gray)] transition-colors hover:bg-[var(--color-border-muted)] hover:text-[var(--color-white)]"
                onClick={() => setMenuOpen(false)}
              >
                Settings
              </Link>
            ) : null}
            <button
              type="button"
              role="menuitem"
              className="w-full px-4 py-2 text-left font-[var(--font-display)] text-sm text-[var(--color-steel-gray)] transition-colors hover:bg-[var(--color-border-muted)] hover:text-[var(--color-white)]"
              onClick={() => {
                setMenuOpen(false);
                void signOut();
              }}
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    );
  }

  const employeeMobileChrome = employeeShell && Boolean(bottomNavigation);
  /** Team member assessment: app header title only — no second wordmark beside it. */
  const hideEmployeeHeaderWordmark =
    employeeShell &&
    Boolean(header) &&
    (/^\/org\/assessment(\/|$)/.test(location.pathname) ||
      location.pathname === "/my/functional-capacity");

  return (
    <div
      className={
        "relative flex flex-col bg-[var(--color-bg)] text-[var(--color-text)] " +
        (employeeMobileChrome
          ? "h-[100dvh] max-h-[100dvh] min-h-0 overflow-hidden"
          : employeeShell
            ? "min-h-screen lg:flex-row"
            : /* Staff layout behaves like an app: the window never scrolls; the sidebar and top
                 bar stay put and only <main> scrolls (with its bounce contained). */
              "h-[100dvh] max-h-[100dvh] min-h-0 overflow-hidden lg:flex-row")
      }
    >
      {showSidebar && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            style={{ display: sidebarOpen ? "block" : "none" }}
            onClick={() => setSidebarOpen(false)}
          />
          <aside
            className={
              "fixed inset-y-0 left-0 z-50 w-[min(18rem,86vw)] shrink-0 flex flex-col min-h-0 overflow-hidden sm:w-64 " +
              "h-[100dvh] max-h-[100dvh] border-r border-[var(--color-border)] bg-[var(--color-charcoal)] " +
              "lg:sticky lg:top-0 lg:self-start lg:inset-y-auto " +
              "transform transition-transform duration-[var(--duration-normal)] ease-[var(--ease-default)] " +
              (sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0")
            }
          >
            <div className="relative flex shrink-0 items-center justify-center overflow-hidden border-b border-[var(--color-border)] px-4 py-4">
              <Link
                to={defaultPath && defaultPath !== "/login" ? defaultPath : "/"}
                className="flex min-h-[3rem] w-full max-w-[min(100%,14rem)] flex-shrink-0 items-center justify-center md:max-w-[16rem]"
              >
                <Fit2LiftWordmark size="md" variant="onDark" />
              </Link>
              <button
                type="button"
                aria-label="Close menu"
                className="absolute right-2 top-1/2 min-h-[44px] min-w-[44px] -translate-y-1/2 flex items-center justify-center lg:hidden text-[var(--color-steel-gray)]"
                onClick={() => setSidebarOpen(false)}
              >
                <span aria-hidden>✕</span>
              </button>
            </div>
            <nav className="p-4 flex-1 min-h-0 overflow-y-auto overscroll-contain">{sidebar}</nav>
            {/* User section — bottom left, hover animate, click = context menu with Sign out */}
            <div className="shrink-0 border-t border-[var(--color-border)] p-4">{renderUserMenu(true)}</div>
          </aside>
        </>
      )}

      <div
        ref={mainColumnRef}
        className={
          "flex min-h-0 min-w-0 flex-1 flex-col " +
          (employeeMobileChrome
            ? "min-h-0 overflow-hidden"
            : employeeShell
              ? "lg:overflow-visible"
              : "h-full overflow-hidden") +
          // In full screen this column is the whole screen; <main> inside it does the scrolling.
          (isFullscreen ? " h-full overflow-hidden bg-[var(--color-bg)]" : "")
        }
      >
        {employeeShell ? (
          <header className="relative z-[var(--z-sticky)] shrink-0 border-b border-[var(--color-border)] bg-[var(--color-charcoal)]">
            <div className="flex min-h-14 items-center gap-2 px-3 py-2 pt-[max(0.5rem,env(safe-area-inset-top))] sm:min-h-16 sm:px-4">
              {!hideEmployeeHeaderWordmark ? (
                <Link
                  to={defaultPath && defaultPath !== "/login" ? defaultPath : "/"}
                  className="flex shrink-0 items-center"
                  aria-label="Home"
                >
                  <Fit2LiftWordmark size="sm" variant="onDark" className="max-h-8 w-auto max-w-[9rem]" />
                </Link>
              ) : null}
              {headerLeft ? <div className="flex shrink-0 items-center">{headerLeft}</div> : null}
              {header ? (
                <div
                  className={
                    "flex min-h-[44px] min-w-0 flex-1 items-center px-1 " +
                    (hideEmployeeHeaderWordmark ? "justify-start" : "justify-center")
                  }
                >
                  <div
                    className={
                      "max-w-full truncate font-[var(--font-display)] text-base font-bold text-[var(--color-text)] sm:text-lg " +
                      (hideEmployeeHeaderWordmark ? "" : "text-center")
                    }
                  >
                    {header}
                  </div>
                </div>
              ) : (
                <div className="min-w-0 flex-1" aria-hidden />
              )}
              {headerRight ? (
                <div className="mr-1 hidden shrink-0 items-center sm:flex">{headerRight}</div>
              ) : null}
              {renderUserMenu(false)}
            </div>
            {employeeDesktopNavigation ? (
              <div className="hidden border-t border-[var(--color-border)] bg-[rgba(11,19,32,0.55)] px-2 lg:block lg:px-4">
                {employeeDesktopNavigation}
              </div>
            ) : null}
          </header>
        ) : null}
        {showSidebar && (
          <header className="sticky top-0 z-[var(--z-sticky)] flex min-h-16 shrink-0 items-center border-b border-[var(--color-border)] bg-[var(--color-charcoal)] px-3 py-2 sm:h-20 sm:px-4 sm:py-0">
            {!isFullscreen ? (
              <button
                type="button"
                aria-label="Open menu"
                className="min-h-[44px] min-w-[44px] flex items-center justify-center lg:hidden text-[var(--color-steel-gray)] shrink-0"
                onClick={() => setSidebarOpen(true)}
              >
                <span aria-hidden>☰</span>
              </button>
            ) : null}
            {headerLeft ? <div className="flex shrink-0 items-center">{headerLeft}</div> : null}
            {header &&
              (headerLeft ? (
                <div className="flex min-h-[44px] min-w-0 flex-1 items-center justify-center px-2 lg:min-h-0">
                  <div className="max-w-[min(100%,42rem)] truncate text-center text-[var(--color-text)]">
                    {header}
                  </div>
                </div>
              ) : (
                <div className="flex flex-1 justify-center min-w-0 px-2">
                  <div className="hidden max-w-[min(100%,42rem)] truncate text-center font-[var(--font-display)] text-xl font-bold text-[var(--color-text)] lg:block xl:text-2xl">
                    {header}
                  </div>
                </div>
              ))}
            {headerRight && (
              <div
                className={
                  (headerRightPersistent ? "flex" : "hidden lg:flex") +
                  " items-center shrink-0 ml-2"
                }
              >
                {headerRight}
              </div>
            )}
            {showFullscreenToggle ? (
              <button
                type="button"
                onClick={toggleFullscreen}
                className="ml-2 flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center border border-[var(--color-border)] text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-signal-blue)] hover:text-[var(--color-text)] chamfer-sm"
                aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
                title={isFullscreen ? "Exit full screen" : "Full screen"}
              >
                {isFullscreen ? <ShrinkIcon /> : <ExpandIcon />}
              </button>
            ) : null}
          </header>
        )}

        <main
          ref={mainRef}
          className={
            /* Employee layout: overflow-x-clip, not hidden — `hidden` on one axis makes the other
               compute to `auto`, turning <main> into a scroll container. Staff layout: <main> IS the
               scroll container on purpose, so sticky children pin to its top, and overscroll-contain
               keeps a bounce (and iPad's "swipe down to leave full screen") inside the content. */
            "flex-1 overflow-x-clip bg-[var(--color-bg)] scroll-smooth " +
            (employeeShell
              ? employeeMobileChrome
                ? "min-h-0 overflow-y-auto overscroll-y-contain scroll-pt-2"
                : "min-h-0 overflow-y-visible lg:overflow-visible "
              : "min-h-0 overflow-y-auto overscroll-contain ") +
            (employeeShell && !employeeMobileChrome ? "lg:scroll-pt-2 " : "")
          }
        >
          {header && !headerLeft && !employeeShell && (
            <div className="border-b border-[var(--color-border)] px-3 py-3 lg:hidden sm:px-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 text-center sm:text-left">
                  <div className="truncate text-center font-[var(--font-display)] text-xl font-bold text-[var(--color-text)] xl:text-2xl">
                    {header}
                  </div>
                </div>
                {headerRight ? <div className="flex justify-center sm:justify-end">{headerRight}</div> : null}
              </div>
            </div>
          )}
          {children}
        </main>
        {bottomNavigation ? (
          <div className="shrink-0 lg:hidden">
            {bottomNavigation}
          </div>
        ) : null}
      </div>
    </div>
  );
}
